import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "src/utils/axios";
import { setTpvLayout } from "src/redux/slices/settings";
import { updateUser } from "src/redux/slices/authJwt";
import { mergeTpvLayout, sanitizeTpvLayout } from "src/utils/tpvLayout/defaults";

// Hook único para leer y mutar la configuración de apariencia del TPV
// camarero. La fuente de verdad es `state.settings.tpv` (Redux); el merge
// contra defaults se hace AQUÍ en lectura para que cualquier campo nuevo
// añadido a defaults sea transparente para los restaurantes existentes.
//
// `save(layout)` despacha al store ANTES del PUT al backend (UX optimista)
// y dispara el POST. Si el backend responde 200 con `user` actualizado,
// authJwt resincroniza el sub-objeto desde el server. Si falla, mantenemos
// el dispatch optimista en el cliente pero el cambio no persiste entre
// dispositivos hasta que se vuelva a intentar.
let lastOverride;
let lastJson;
let lastLayout = null;

export function resolveSharedLayout(overrideTpv, userJson) {
  if (lastLayout && overrideTpv === lastOverride && userJson === lastJson) {
    return lastLayout;
  }
  let next;
  if (overrideTpv) next = mergeTpvLayout(overrideTpv);
  else if (userJson) {
    try {
      next = mergeTpvLayout(JSON.parse(userJson));
    } catch (_) {
      // JSON corrupto en BD → defaults (no rompemos el TPV por esto).
      next = mergeTpvLayout(null);
    }
  } else next = mergeTpvLayout(null);
  lastOverride = overrideTpv;
  lastJson = userJson;
  lastLayout = next;
  return next;
}

export default function useTpvLayout() {
  const dispatch = useDispatch();
  // Prioridad: client-side optimista (`settings.tpv`) → JSON serializado del
  // user (BD, llegado en login). Esto cubre dos escenarios:
  //   1) El usuario acaba de guardar en el panel → settings.tpv ya tiene la
  //      versión nueva mientras el POST está en vuelo.
  //   2) Refresh / nuevo dispositivo → settings.tpv viene de localStorage o
  //      null, pero user.tpvLayoutSettingsJson trae la verdad del server.
  const overrideTpv = useSelector((state) => state.settings.tpv);
  const userJson = useSelector(
    (state) => state.authJwt?.user?.tpvLayoutSettingsJson,
  );

  // El layout efectivo SIEMPRE es la versión mergeada — los componentes que
  // leen del hook nunca tienen que preocuparse de campos undefined.
  // Fase 3A · memo compartido a nivel de módulo: con N tiles usando el hook,
  // el JSON.parse + merge se hace una vez por cambio de (override, json) y
  // todas reciben la MISMA referencia (antes: uno por tile y por mount).
  const layout = useMemo(
    () => resolveSharedLayout(overrideTpv, userJson),
    [overrideTpv, userJson],
  );

  const raw = overrideTpv;

  const save = useCallback(
    async (next) => {
      const sanitized = sanitizeTpvLayout(next);
      // Optimista: aplicamos en cliente al instante para que el preview y
      // los componentes reaccionen sin esperar la red.
      dispatch(setTpvLayout(sanitized));

      try {
        const json = JSON.stringify(sanitized);
        const res = await axios.post("settings/UpdateTpvLayout", { json });
        // El backend devuelve `{ user }` con tpvLayoutSettingsJson ya
        // actualizado. Sincronizamos authJwt.user para que el resto de
        // la app (otras pestañas, useAuth) lo vea sin recargar.
        if (res?.data?.user) dispatch(updateUser(res.data.user));
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err };
      }
    },
    [dispatch],
  );

  const reset = useCallback(async () => {
    // 1) POST con json=null → backend borra User.TpvLayoutSettingsJson.
    // 2) Tras OK, limpiamos el override del cliente y resincronizamos
    //    authJwt.user con la respuesta del back (tpvLayoutSettingsJson=null
    //    ahora). Resultado: la app cae a defaults completos en sitio.
    try {
      const res = await axios.post("settings/UpdateTpvLayout", { json: null });
      dispatch(setTpvLayout(null));
      if (res?.data?.user) dispatch(updateUser(res.data.user));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err };
    }
  }, [dispatch]);

  return { layout, save, reset, raw };
}
